const toMins = (t) => {
  if (!t) return null;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + (m || 0);
};

/**
 * Derive a human-friendly slot name from its time band.
 *
 * Uses the slot's MIDPOINT (when an end time is given) rather than its start so
 * a long block is named for where its bulk sits — e.g. an 11:00–15:00 slot
 * reads "Afternoon", not "Morning". Falls back to the start time alone when no
 * end time is available.
 */
export function slotDisplayName(startTime, endTime) {
  const start = toMins(startTime);
  if (start == null) return 'Slot';
  const end = toMins(endTime);
  const mins = end != null && end > start ? Math.floor((start + end) / 2) : start;
  if (mins >= 23 * 60) return 'Late Night Slot';
  if (mins >= 22 * 60) return 'Midnight Slot';
  if (mins >= 19 * 60) return 'Night Slot';
  if (mins >= 16 * 60) return 'Day Night Slot';
  if (mins >= 12 * 60) return 'Afternoon Slot';
  return 'Morning Slot';
}

// Matches a HH:MM-HH:MM range (hyphen or en-dash) anywhere in a string.
const RANGE_RE = /(\d{1,2}:\d{2})\s*[-–]\s*(\d{1,2}:\d{2})/;
// True when a string is *only* a bare time range (no real slot name).
const BARE_RANGE_RE = /^\s*\d{1,2}:\d{2}\s*[-–]\s*\d{1,2}:\d{2}\s*$/;

/**
 * Extract a canonical "HH:MM-HH:MM" range from a possibly-corrupted time_slot
 * value. Idempotent: handles clean ranges, "Name (HH:MM-HH:MM)", and the
 * doubled "06:00–10:00 (06:00-10:00)" form (GA-004) by pulling the first range.
 * Returns the trimmed input unchanged when no range is present.
 */
export function cleanSlotRange(timeSlot) {
  if (!timeSlot) return '';
  const s = String(timeSlot).trim();
  const m = s.match(RANGE_RE);
  return m ? `${m[1]}-${m[2]}` : s;
}

/** True when the label carries no information beyond a bare time range. */
export function isBareRange(str) {
  return !str || BARE_RANGE_RE.test(String(str));
}

/**
 * Build a single, non-duplicated slot label for display: "Name (HH:MM-HH:MM)"
 * when a real (non-range) name exists, otherwise just the clean range. Safe to
 * call on already-corrupted data — it never nests parentheses.
 */
export function composeSlotLabel(slotName, timeSlot) {
  const range = cleanSlotRange(timeSlot);
  const name = !isBareRange(slotName) ? String(slotName).trim() : '';
  if (name && range) return `${name} (${range})`;
  return range || name || 'N/A';
}
